import { createElement } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { ErrorPage } from './ErrorPage';
import type { LogArgument } from '../../utils/logger';

type ErrorWithFallbackElement = { FallbackElement: ComponentType };
type ErrorPageComponentProps = {
    error?: LogArgument;
    resetBoundary?: () => void;
};
export type ErrorFallbackProps = {
    error: LogArgument;
    ErrorPageComponent?: ComponentType<ErrorPageComponentProps>;
};

function DefaultErrorPage({ error, resetBoundary }: ErrorPageComponentProps) {
    return createElement(ErrorPage, { tryAgain: resetBoundary, error });
}

function hasFallbackElement(value: LogArgument): value is ErrorWithFallbackElement {
    if ((typeof value !== 'object' && typeof value !== 'function') || value === null) {
        return false;
    }

    if (!('FallbackElement' in value)) {
        return false;
    }

    return typeof value.FallbackElement === 'function';
}

export function ErrorPageFallback({
    error,
    ErrorPageComponent,
}: ErrorFallbackProps): ReactElement | null {
    const { resetBoundary } = useErrorBoundary();
    if (hasFallbackElement(error)) {
        return createElement(error.FallbackElement);
    }

    const ResolvedErrorPage = ErrorPageComponent ?? DefaultErrorPage;
    return createElement(ResolvedErrorPage, { error, resetBoundary });
}
